import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { GetVehicleResponse } from '@/api/generated/schemas';
import { getGetInventoryVehicleQueryKey } from '@/api/generated/inventory-vehicles/inventory-vehicles';
import { EmptyState, FormActionBar, Tabs, WindowContent, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';

import { VEHICLE_LIST_KEY } from '../internal/vehiclesQueryKeys';
import { VehicleDocumentsTab } from './components/VehicleDocumentsTab';
import { VehicleExpiryBanner } from './components/VehicleExpiryBanner';
import { VehicleGeneralTab } from './components/VehicleGeneralTab';
import { VehicleMaintenanceTab } from './components/VehicleMaintenanceTab';
import { vehicleDetailAgenda, type VehicleTabKey } from './vehicleDetailAgenda';
import { useVehicleForm } from './useVehicleForm';
import type { VehicleFormValues } from './vehicleFormSchema';

export interface VehicleDetailViewProps {
  layerId: string;
  vehicleId?: number;
  initialValues: VehicleFormValues;
  /** Načtený detail (jen v editaci) — zdroj naléhavosti expirací pro banner. */
  vehicle?: GetVehicleResponse;
}

/**
 * Orchestrátor záložek detailu vozu a patičky akcí.
 */
export function VehicleDetailView({
  layerId,
  vehicleId,
  initialValues,
  vehicle,
}: VehicleDetailViewProps) {
  const { t } = useTranslation('inventory');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = vehicleId != null;

  const { state } = useAgendaParams(vehicleDetailAgenda, layerId);
  const agendaState = useAgendaState(vehicleDetailAgenda, layerId);
  const activeTab: VehicleTabKey = state.tab ?? 'general';
  // Formulář vozu vlastní jen General; Documents a Maintenance mají vlastní uložení v dialogu.
  const isVehicleFormTab = activeTab === 'general';
  const setActiveTab = useCallback(
    (tab: VehicleTabKey) => agendaState.patch({ tab }),
    [agendaState]
  );

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useVehicleForm({
    vehicleId,
    initialValues,
    onSaved: (id) => {
      void queryClient.invalidateQueries({ queryKey: getGetInventoryVehicleQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: [VEHICLE_LIST_KEY] });
      toast({ severity: 'success', description: t('vehicle.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('vehicle.toast.errorTitle'),
        description: t('vehicle.toast.saveError'),
      });
    },
  });

  return (
    <WindowContent
      as="div"
      width="reading"
      banner={
        <Tabs<VehicleTabKey>
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'general', label: t('vehicle.tabs.general') },
            { value: 'rides', label: t('vehicle.tabs.rides') },
            { value: 'documents', label: t('vehicle.tabs.documents') },
            { value: 'maintenance', label: t('vehicle.tabs.maintenance') },
            { value: 'walkAroundLog', label: t('vehicle.tabs.walkAroundLog') },
          ]}
        />
      }
      footer={
        // Globální lišta patří jen tabu, který formulář vozu skutečně edituje — jinde by nabízela
        // uložení něčeho, co uživatel nemá na obrazovce.
        isVehicleFormTab ? (
          <form.Subscribe
            selector={(formState) => ({
              isDirty: formState.isDirty,
              canSubmit: formState.canSubmit,
              isSubmitting: formState.isSubmitting,
            })}
          >
            {({ isDirty, canSubmit, isSubmitting }) => (
              <FormActionBar
                onBack={close}
                onSave={submit}
                saving={isSubmitting}
                disabled={!canSubmit}
                statusNote={
                  <>
                    <span
                      className={cn(
                        'mr-phx-2 inline-block h-1.5 w-1.5 rounded-full',
                        isDirty ? 'bg-phx-warning' : 'bg-phx-text-faint'
                      )}
                    />
                    {isDirty ? t('vehicle.status.unsaved') : t('vehicle.status.clean')}
                  </>
                }
              />
            )}
          </form.Subscribe>
        ) : undefined
      }
    >
      <div className="gap-phx-3 flex flex-col">
        {isEdit && vehicle ? <VehicleExpiryBanner vehicle={vehicle} /> : null}
        {isVehicleFormTab ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <VehicleGeneralTab form={form} />
          </form>
        ) : null}
        {activeTab === 'documents' ? <VehicleDocumentsTab vehicleId={vehicleId} /> : null}
        {activeTab === 'rides' ? (
          <EmptyState
            icon="bus"
            title={t('vehicle.rides.placeholderTitle')}
            description={t('vehicle.rides.placeholderDescription')}
          />
        ) : null}
        {activeTab === 'maintenance' ? <VehicleMaintenanceTab vehicleId={vehicleId} /> : null}
        {activeTab === 'walkAroundLog' ? (
          <EmptyState
            icon="doc"
            title={t('vehicle.walkAroundLog.placeholderTitle')}
            description={t('vehicle.walkAroundLog.placeholderDescription')}
          />
        ) : null}
      </div>
    </WindowContent>
  );
}
